// Subtle dotted world map — inline SVG so no extra dependency.
// Based on land coordinates approximated by a hand-tuned dot matrix.
// Renders as a decorative background element only.
import { motion } from "framer-motion";

// Dot positions are sampled from a low-res land-mass silhouette (approximate).
// Coords are [x, y] on a 200x100 canvas.
const DOTS = [
  // North America
  [22, 22], [26, 22], [30, 22], [22, 26], [26, 26], [30, 26], [34, 26], [38, 26],
  [26, 30], [30, 30], [34, 30], [38, 30], [30, 34], [34, 34], [38, 34], [42, 34],
  [34, 38], [38, 38], [42, 38], [46, 38], [38, 42], [42, 42],
  // South America
  [50, 50], [54, 50], [50, 54], [54, 54], [50, 58], [54, 58], [50, 62], [54, 62],
  [54, 66], [50, 70], [54, 70],
  // Europe
  [96, 26], [100, 26], [104, 26], [96, 30], [100, 30], [104, 30], [108, 30],
  [92, 34], [96, 34], [100, 34], [104, 34], [108, 34], [112, 34],
  [96, 38], [100, 38], [104, 38],
  // Africa
  [96, 46], [100, 46], [104, 46], [92, 50], [96, 50], [100, 50], [104, 50],
  [96, 54], [100, 54], [104, 54], [100, 58], [104, 58], [100, 62], [104, 62],
  [104, 66], [100, 70],
  // Middle East
  [114, 42], [118, 42], [114, 46], [118, 46],
  // India
  [128, 46], [132, 46], [128, 50], [132, 50], [124, 46], [124, 50],
  // Southeast Asia / China
  [138, 34], [142, 34], [146, 34], [138, 38], [142, 38], [146, 38], [150, 38],
  [138, 42], [142, 42], [146, 42], [150, 42], [154, 42],
  [142, 46], [146, 46], [150, 46], [154, 46], [146, 50], [150, 50],
  // Japan / Korea
  [162, 34], [166, 34], [162, 38], [166, 38],
  // Australia
  [160, 66], [164, 66], [168, 66], [160, 70], [164, 70], [168, 70], [172, 70],
];

// A few "hub" locations that pulse in copper (India, China, UAE, EU, US, SE Asia).
const HUBS = [
  { x: 130, y: 48, label: "India" },
  { x: 148, y: 44, label: "China" },
  { x: 116, y: 44, label: "UAE" },
  { x: 100, y: 32, label: "EU" },
  { x: 34, y: 32, label: "US" },
  { x: 148, y: 50, label: "SE Asia" },
];

export default function WorldMap({ className = "", showLabels = false }) {
  return (
    <div className={"relative w-full " + className} aria-hidden="true" data-testid="world-map">
      <svg viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        {/* Base graticule (very subtle) */}
        <g stroke="currentColor" strokeWidth="0.08" opacity="0.15">
          {[15, 30, 45, 60, 75, 90].map((y) => (
            <line key={"h" + y} x1="0" y1={y} x2="200" y2={y} />
          ))}
          {[20, 40, 60, 80, 100, 120, 140, 160, 180].map((x) => (
            <line key={"v" + x} x1={x} y1="0" x2={x} y2="100" />
          ))}
        </g>

        {/* Land dots */}
        <g fill="currentColor" opacity="0.55">
          {DOTS.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="0.9" />
          ))}
        </g>

        {/* Hub connection lines (curved) */}
        <g stroke="#C86A2E" strokeWidth="0.15" fill="none" opacity="0.55">
          {HUBS.slice(0, -1).map((h, i) => {
            const n = HUBS[i + 1];
            const midX = (h.x + n.x) / 2;
            const midY = Math.min(h.y, n.y) - 8;
            return (
              <path
                key={i}
                d={`M ${h.x} ${h.y} Q ${midX} ${midY} ${n.x} ${n.y}`}
              />
            );
          })}
        </g>

        {/* Hub markers */}
        <g>
          {HUBS.map((h, i) => (
            <g key={h.label}>
              <motion.circle
                cx={h.x}
                cy={h.y}
                r="1.6"
                fill="#C86A2E"
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.4, 1] }}
                transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.35 }}
                style={{ transformOrigin: `${h.x}px ${h.y}px`, transformBox: "fill-box" }}
              />
              <circle cx={h.x} cy={h.y} r="0.8" fill="#F7F5F1" />
              {showLabels && (
                <text
                  x={h.x + 3}
                  y={h.y + 1.5}
                  fontSize="2.4"
                  fill="currentColor"
                  fontFamily="IBM Plex Mono, monospace"
                  opacity="0.8"
                >
                  {h.label.toUpperCase()}
                </text>
              )}
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
